/**
 * User Drawer Component
 *
 * Enterprise features:
 * - Lazy loading of user details
 * - Real-time session management
 * - Optimistic updates for session revocation
 * - Error handling with retry
 */

import { useState, useEffect, useCallback } from 'react';
import { Drawer } from './index';
import Button from '../common/Button';
import { useToast } from '../../context/ToastContext';
import { usersApi, fraudApi } from '../../services/api';
import { useQuery, useMutation } from '../../hooks/useDataFetch';
import './UserDrawer.css';

const UserDrawer = ({ isOpen, onClose, user, onUserUpdate }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('profile');

  // Fetch full user details when drawer opens
  const {
    data: userDetails,
    isLoading: loadingUser,
    error: userError,
    refetch: refetchUser,
  } = useQuery(
    () => user?.id ? usersApi.get(user.id) : Promise.resolve(null),
    {
      key: user?.id ? `user:${user.id}` : null,
      enabled: isOpen && !!user?.id,
      cacheTTL: 30000,
    }
  );

  // Fetch user sessions
  const {
    data: sessionsData,
    isLoading: loadingSessions,
    refetch: refetchSessions,
  } = useQuery(
    () => user?.id ? usersApi.sessions(user.id) : Promise.resolve(null),
    {
      key: user?.id ? `user:${user.id}:sessions` : null,
      enabled: isOpen && !!user?.id,
      cacheTTL: 15000,
    }
  );

  // Merge passed user with fetched details
  const fullUser = {
    ...user,
    ...userDetails,
  };

  const sessions = sessionsData?.sessions || sessionsData?.data || sessionsData || [];

  // Session revocation mutation
  const revokeSessionMutation = useMutation(
    ({ userId, sessionId }) => usersApi.revokeSession(userId, sessionId),
    {
      onSuccess: (_, { sessionId }) => {
        showToast('Session revoked', 'success');
        refetchSessions();
      },
      onError: (err) => {
        showToast(err.message || 'Failed to revoke session', 'error');
      },
    }
  );

  // Revoke all sessions mutation
  const revokeAllSessionsMutation = useMutation(
    (userId) => Promise.all(
      sessions.map(s => usersApi.revokeSession(userId, s.id))
    ),
    {
      onSuccess: () => {
        showToast('All sessions revoked', 'success');
        refetchSessions();
      },
      onError: (err) => {
        showToast(err.message || 'Failed to revoke sessions', 'error');
      },
    }
  );

  // User update mutation
  const updateUserMutation = useMutation(
    ({ userId, data }) => usersApi.update(userId, data),
    {
      onSuccess: (result) => {
        showToast('User updated', 'success');
        onUserUpdate?.(result);
        refetchUser();
      },
      onError: (err) => {
        showToast(err.message || 'Failed to update user', 'error');
      },
    }
  );

  // Handlers
  const handleRevokeSession = useCallback((sessionId) => {
    if (!user?.id) return;
    revokeSessionMutation.mutate({ userId: user.id, sessionId });
  }, [user?.id, revokeSessionMutation]);

  const handleRevokeAllSessions = useCallback(() => {
    if (!user?.id || sessions.length === 0) return;
    if (window.confirm('Revoke all sessions? The user will be logged out everywhere.')) {
      revokeAllSessionsMutation.mutate(user.id);
    }
  }, [user?.id, sessions, revokeAllSessionsMutation]);

  const handleSuspendUser = useCallback(() => {
    if (!user?.id) return;
    if (window.confirm(`Suspend ${fullUser.name || fullUser.email}?`)) {
      updateUserMutation.mutate({
        userId: user.id,
        data: { status: 'suspended' },
      });
    }
  }, [user?.id, fullUser, updateUserMutation]);

  const handleActivateUser = useCallback(() => {
    if (!user?.id) return;
    updateUserMutation.mutate({
      userId: user.id,
      data: { status: 'active' },
    });
  }, [user?.id, updateUserMutation]);

  // Helpers
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '-';
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  const getFraudRiskLevel = (score) => {
    if (score === undefined || score === null) return { label: 'Unknown', class: 'unknown' };
    if (score < 30) return { label: 'Low Risk', class: 'low' };
    if (score < 70) return { label: 'Medium Risk', class: 'medium' };
    return { label: 'High Risk', class: 'high' };
  };

  const getUserInitials = (name, email) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email?.substring(0, 2).toUpperCase() || 'U';
  };

  if (!user) return null;

  const risk = getFraudRiskLevel(fullUser.fraudScore);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="User Details" width="480px">
      <div className="user-drawer-content">
        {/* User Header */}
        <div className="user-drawer-header">
          <div className="user-drawer-avatar" data-status={fullUser.status}>
            {getUserInitials(fullUser.name, fullUser.email)}
          </div>
          <div className="user-drawer-identity">
            <h2 className="user-drawer-name">{fullUser.name || 'Unknown User'}</h2>
            <div className="user-drawer-email">{fullUser.email}</div>
            <div className="user-drawer-badges">
              <span className={`badge badge-${fullUser.status === 'active' ? 'success' : 'danger'}`}>
                {fullUser.status || 'Active'}
              </span>
              {fullUser.role && (
                <span className="badge badge-info">{fullUser.role}</span>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="drawer-tabs">
          <button
            className={`drawer-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`drawer-tab ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            Sessions ({sessions.length})
          </button>
          <button
            className={`drawer-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
        </div>

        {/* Loading State */}
        {loadingUser && (
          <div className="drawer-loading">
            <div className="spinner"></div>
            <span>Loading user details...</span>
          </div>
        )}

        {/* Error State */}
        {userError && (
          <div className="drawer-error">
            <span>Failed to load user details</span>
            <Button variant="ghost" size="sm" onClick={refetchUser}>Retry</Button>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && !loadingUser && (
          <div className="drawer-section">
            <h4 className="drawer-section-title">Profile Information</h4>
            <div className="user-info-grid">
              <div className="user-info-item">
                <span className="user-info-label">Phone</span>
                <span className="user-info-value">
                  {fullUser.phone || '-'}
                  {fullUser.phoneVerified && <span className="verified-badge">Verified</span>}
                </span>
              </div>
              <div className="user-info-item">
                <span className="user-info-label">Email Verified</span>
                <span className="user-info-value">
                  {fullUser.emailVerified ? (
                    <span className="text-success">Yes</span>
                  ) : (
                    <span className="text-danger">No</span>
                  )}
                </span>
              </div>
              <div className="user-info-item">
                <span className="user-info-label">Created</span>
                <span className="user-info-value">{formatDate(fullUser.createdAt)}</span>
              </div>
              <div className="user-info-item">
                <span className="user-info-label">Last Login</span>
                <span className="user-info-value">{formatTimeAgo(fullUser.lastLoginAt)}</span>
              </div>
              <div className="user-info-item">
                <span className="user-info-label">Tenant</span>
                <span className="user-info-value">
                  {fullUser.tenant?.name || fullUser.tenantName || '-'}
                </span>
              </div>
              <div className="user-info-item">
                <span className="user-info-label">Role</span>
                <span className="user-info-value">{fullUser.role || 'User'}</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="drawer-actions">
              {fullUser.status === 'active' ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleSuspendUser}
                  disabled={updateUserMutation.isLoading}
                >
                  Suspend User
                </Button>
              ) : (
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleActivateUser}
                  disabled={updateUserMutation.isLoading}
                >
                  Activate User
                </Button>
              )}
              <Button variant="secondary" size="sm">
                Edit Profile
              </Button>
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="drawer-section">
            <div className="section-header-row">
              <h4 className="drawer-section-title">Active Sessions</h4>
              {sessions.length > 0 && (
                <Button
                  variant="danger"
                  size="xs"
                  onClick={handleRevokeAllSessions}
                  disabled={revokeAllSessionsMutation.isLoading}
                >
                  Revoke All
                </Button>
              )}
            </div>

            {loadingSessions ? (
              <div className="drawer-loading small">
                <div className="spinner"></div>
              </div>
            ) : sessions.length > 0 ? (
              <div className="sessions-list">
                {sessions.map((session) => (
                  <div key={session.id} className="session-item">
                    <div className="session-item-header">
                      <span className="session-device">
                        {session.device || session.userAgent || 'Unknown Device'}
                      </span>
                      <Button
                        variant="danger"
                        size="xs"
                        onClick={() => handleRevokeSession(session.id)}
                        disabled={revokeSessionMutation.isLoading}
                      >
                        Revoke
                      </Button>
                    </div>
                    <div className="session-meta">
                      <div>IP: {session.ip || '-'} • {session.location || 'Unknown location'}</div>
                      <div>
                        Created: {formatDate(session.createdAt)} •
                        Last active: {formatTimeAgo(session.lastActiveAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No active sessions</p>
              </div>
            )}
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="drawer-section">
            <h4 className="drawer-section-title">Fraud & Security</h4>

            <div className={`fraud-signal-box ${risk.class}`}>
              <div className="fraud-signal-header">
                <span className="fraud-score">{fullUser.fraudScore ?? '-'}</span>
                <span className="fraud-label">{risk.label}</span>
              </div>
              {fullUser.fraudSignals && (
                <div className="fraud-signal-details">
                  {fullUser.fraudSignals.map((signal, idx) => (
                    <div key={idx} className="fraud-signal-item">
                      {signal.positive ? '✓' : '⚠'} {signal.message}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="security-actions">
              <Button variant="secondary" size="sm" icon="shield">
                Add to Watchlist
              </Button>
              <Button variant="secondary" size="sm" icon="refresh">
                Recalculate Score
              </Button>
              <Button variant="danger" size="sm" icon="ban">
                Block User
              </Button>
            </div>

            {/* Auth History */}
            <h4 className="drawer-section-title" style={{ marginTop: 'var(--space-6)' }}>
              Recent Auth Events
            </h4>
            {fullUser.authHistory && fullUser.authHistory.length > 0 ? (
              <div className="auth-history">
                {fullUser.authHistory.slice(0, 5).map((event, idx) => (
                  <div key={idx} className="timeline-item">
                    <span className="timeline-time">{formatTimeAgo(event.timestamp)}</span>
                    <span className={`timeline-event ${event.success ? 'success' : 'warning'}`}>
                      {event.success ? '✓' : '⚠'} {event.action || event.type}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No recent auth events</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default UserDrawer;
